import {
  ActionIcon,
  Button,
  Center,
  Container,
  Group,
  Image,
  Paper,
  Radio,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { useClipboard } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { fetchDeviceById } from '../data/device.service'
import { COLORS } from '../utils/constants'

import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons'
import {
  faCheck,
  faCopy,
  faDownload,
  faShareNodes,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import canvasConfetti from 'canvas-confetti'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

const SOCIAL_IMAGE_URLS = {
  post: 'https://hiddengemzstorage.blob.core.windows.net/system/NewHost.Post.png',
  story:
    'https://hiddengemzstorage.blob.core.windows.net/system/Host.Story.jpg',
  square:
    'https://hiddengemzstorage.blob.core.windows.net/system/Host.Square.png',
}
const WEBSITE_URL = 'https://hiddengemz.tv'

type ImageSize = 'story' | 'post' | 'square'

export const DeviceCelebrationPage = () => {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const deviceId = parseInt(searchParams.get('deviceId') || '0')
  const locationId = parseInt(searchParams.get('locationId') || '0')

  const clipboard = useClipboard({ timeout: 2000 })
  const [price, setPrice] = useState<number | null>(null)
  const [imageSize, setImageSize] = useState<ImageSize>('post')

  useEffect(() => {
    const getData = async () => {
      if (deviceId) {
        try {
          const device = await fetchDeviceById(deviceId.toString())
          if (device) {
            setPrice(device.monthlyPrice)
          }
        } catch (e) {
          console.error('Failed to fetch device data', e)
        }
      }
    }
    getData()
  }, [deviceId])

  useEffect(() => {
    const brandColors = [
      COLORS.LightGreen,
      COLORS.HiddenGemzGold,
      COLORS.HiddenGemzRed,
    ]

    // Initial big burst from center
    canvasConfetti({
      particleCount: 200,
      spread: 80,
      origin: { y: 1 },
      colors: brandColors,
      startVelocity: 90,
    })

    // Burst from left side
    canvasConfetti({
      particleCount: 200,
      angle: 60,
      spread: 50,
      origin: { x: 0, y: 0.9 },
      colors: brandColors,
      startVelocity: 90,
    })

    // Burst from right side
    canvasConfetti({
      particleCount: 200,
      angle: 120,
      spread: 50,
      origin: { x: 1, y: 0.9 },
      colors: brandColors,
      startVelocity: 90,
    })
  }, [])

  const shareUrl = locationId
    ? `${WEBSITE_URL}/locations/${locationId}`
    : WEBSITE_URL

  const getShareCaption = () => {
    const priceText = price ? ` for just $${price}/month` : ''
    return `🎉 We just became a host on the Hidden Gemz advertising network! Businesses can now advertise on our screen${priceText}. Upload your ad now at ${shareUrl} #HiddenGemz #SupportLocal #LocalAdvertising`
  }

  const shareCaption = getShareCaption()

  const handleDownloadImage = async (url: string, filename: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      window.URL.revokeObjectURL(link.href)
      return true
    } catch (err) {
      console.error('Failed to download image', err)
      notifications.show({
        title: 'Error',
        message: 'Failed to download image. Please try again.',
        color: 'red',
      })
      return false
    }
  }

  const handleCopyCaption = () => {
    clipboard.copy(shareCaption)
    notifications.show({
      title: 'Caption Copied!',
      message: 'Your caption has been copied to clipboard.',
      color: 'green',
      icon: <FontAwesomeIcon icon={faCheck} />,
      autoClose: 3000,
    })
  }

  const handleInstagramShare = () => {
    window.open('https://instagram.com', '_blank')
  }

  const handleShare = (platform: string) => {
    let url = ''

    switch (platform) {
      case 'twitter':
      case 'x':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareCaption,
        )}&url=${encodeURIComponent(shareUrl)}`
        break
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl,
        )}&quote=${encodeURIComponent(shareCaption)}`
        break
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          shareUrl,
        )}`
        break
      case 'native':
        if (navigator.share) {
          navigator
            .share({
              title: 'Hidden Gemz',
              text: shareCaption,
              url: shareUrl,
            })
            .catch(console.error)
        } else {
          clipboard.copy(`${shareCaption} ${shareUrl}`)
          notifications.show({
            title: 'Copied!',
            message: 'Share text has been copied to clipboard.',
            color: 'green',
            icon: <FontAwesomeIcon icon={faCheck} />,
          })
        }
        return
    }
    window.open(url, '_blank')
  }

  return (
    <Container size='xl' py={10}>
      <Stack align='center' justify='center' gap='lg'>
        {/* Header Section */}
        <Stack gap={0} align='center' style={{ textAlign: 'center' }}>
          <Text
            component='h1'
            fz={{ base: 36, md: 48, lg: 56 }}
            fw={900}
            variant='gradient'
            gradient={{
              from: COLORS.HiddenGemzGold,
              to: COLORS.HiddenGemzRed,
              deg: 45,
            }}
          >
            You're Live!
          </Text>
          <Text c='dimmed' size='md' fw={500}>
            Your device is set up and ready to host advertisements!
          </Text>
        </Stack>

        {/* Social Images Grid */}
        <Paper
          shadow='sm'
          radius='md'
          p='xl'
          withBorder
          w='100%'
          style={{ backgroundColor: 'var(--mantine-color-gray-2)' }}
        >
          <Stack gap='lg'>
            <Stack gap={0}>
              <Title order={2} ta='center'>
                Share Your Success
              </Title>
              <Text c='dimmed' ta='center' size='lg'>
                Download this image to share on social media
              </Text>
            </Stack>

            <Stack
              gap='xl'
              align='center'
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              }}
            >
              <Center>
                <Image
                  src={SOCIAL_IMAGE_URLS['post']}
                  radius='md'
                  w='100%'
                  maw={400}
                  h='auto'
                  fit='contain'
                  alt='Hidden Gemz'
                />
              </Center>

              <Stack gap='md'>
                <Stack
                  gap='sm'
                  style={{
                    backgroundColor: 'var(--mantine-color-blue-0)',
                    padding: '0.75rem',
                    borderRadius: '6px',
                  }}
                >
                  <Text fw={600} size='sm'>
                    💡 Pro Tip
                  </Text>
                  <Text size='xs'>
                    Take a photo of your device and share it alongside our brand
                    image! Tag us @hiddengemz.tv for best reach.
                  </Text>
                </Stack>

                <Stack gap='sm'>
                  <Title order={4}>Image Size</Title>
                  <Text c='dimmed' size='sm'>
                    Choose size
                  </Text>
                  <Radio
                    label='Story (1080 x 1920)'
                    value='story'
                    checked={imageSize === 'story'}
                    onChange={() => setImageSize('story')}
                    size='sm'
                  />
                  <Radio
                    label='Square (1080 x 1080)'
                    value='square'
                    checked={imageSize === 'square'}
                    onChange={() => setImageSize('square')}
                    size='sm'
                  />
                  <Radio
                    label='Post (1080 x 1350)'
                    value='post'
                    checked={imageSize === 'post'}
                    onChange={() => setImageSize('post')}
                    size='sm'
                  />
                </Stack>

                <Button
                  leftSection={<FontAwesomeIcon icon={faDownload} />}
                  variant='filled'
                  color='dark'
                  onClick={() => {
                    const filename = `hidden-gemz-${imageSize}.${imageSize === 'story' ? 'jpg' : 'png'}`
                    handleDownloadImage(SOCIAL_IMAGE_URLS[imageSize], filename)
                  }}
                  size='sm'
                >
                  Download
                </Button>

                <Stack gap='sm'>
                  <Title order={4}>Share Your Caption</Title>
                  <Text c='dimmed' size='xs'>
                    Copy for your post
                  </Text>

                  <Paper
                    p='md'
                    withBorder
                    style={{ backgroundColor: 'var(--mantine-color-white)' }}
                  >
                    <Text size='xs' style={{ whiteSpace: 'pre-wrap' }}>
                      {shareCaption}
                    </Text>
                  </Paper>

                  <Button
                    leftSection={
                      clipboard.copied ? (
                        <FontAwesomeIcon icon={faCheck} />
                      ) : (
                        <FontAwesomeIcon icon={faCopy} />
                      )
                    }
                    variant={clipboard.copied ? 'filled' : 'light'}
                    color={clipboard.copied ? 'green' : 'blue'}
                    size='sm'
                    onClick={handleCopyCaption}
                  >
                    {clipboard.copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
            <Group gap='lg' justify='center' wrap='wrap'>
              <Tooltip label='Share on Instagram' withArrow>
                <ActionIcon
                  size={70}
                  radius='xl'
                  variant='gradient'
                  gradient={{
                    from: '#833ab4',
                    to: '#fd1d1d',
                    deg: 45,
                  }}
                  onClick={handleInstagramShare}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={faInstagram} size='2xl' />
                </ActionIcon>
              </Tooltip>

              <Tooltip label='Share on X (Twitter)' withArrow>
                <ActionIcon
                  size={70}
                  radius='xl'
                  variant='filled'
                  color='dark'
                  onClick={() => handleShare('x')}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={faXTwitter} size='2xl' />
                </ActionIcon>
              </Tooltip>

              <Tooltip label='Share on Facebook' withArrow>
                <ActionIcon
                  size={70}
                  radius='xl'
                  variant='filled'
                  color='blue'
                  onClick={() => handleShare('facebook')}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={faFacebook} size='2xl' />
                </ActionIcon>
              </Tooltip>

              <Tooltip label='Share on LinkedIn' withArrow>
                <ActionIcon
                  size={70}
                  radius='xl'
                  variant='filled'
                  color='blue'
                  onClick={() => handleShare('linkedin')}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={faLinkedin} size='2xl' />
                </ActionIcon>
              </Tooltip>

              <Tooltip label='Share' withArrow>
                <ActionIcon
                  size={70}
                  radius='xl'
                  variant='light'
                  color='gray'
                  onClick={() => handleShare('native')}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={faShareNodes} size='2xl' />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Stack>
        </Paper>

        {/* CTA Button */}
        <Button
          size='xl'
          variant='filled'
          color={COLORS.LightGreen}
          onClick={() => navigate('/dashboard')}
          style={{ marginTop: '1rem' }}
        >
          Go to Dashboard
        </Button>
      </Stack>
    </Container>
  )
}
